import { Form as FormikForm, Formik, useFormikContext } from "formik";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Container, Spinner } from "reactstrap";
import Button from "~/components/Button";
import CheckboxField from "~/components/CheckboxField";
import OrganizationPicker from "~/components/OrganizationPicker";
import PriorityMessageFields, {
  emptyPriorityMessage,
  priorityMessageSchema,
  type PriorityMessageFormValues,
} from "~/components/PriorityMessageFields";
import useApi, { post } from "~/hooks/use-api";
import { ArrowLeftIcon } from "@primer/octicons-react";
import type { PriorityMessage } from "~/gen/rpc/priority-messages";
import type { AssignResult } from "~/api/priority-message/[messageId]/orgs";
import type { OrgConflict } from "~/api/priority-message/conflict";
import {
  checkOrgConflicts,
  parseFlags,
  serializeFlags,
} from "~/utils/priority-messages";

type FormValues = PriorityMessageFormValues & { isGlobal: boolean };

export default function NewPriorityMessagePage() {
  const router = useRouter();
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<PriorityMessage | null>(null);
  const [conflicts, setConflicts] = useState<OrgConflict[]>([]);
  const outcomeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!error) return;
    outcomeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [error]);

  // ?copyFrom=<messageId> starts the form as a copy of an existing message.
  // Org assignments are not copied - a copy starts unassigned.
  const copyFrom =
    typeof router.query.copyFrom === "string" ? router.query.copyFrom : null;

  const orgId =
    typeof router.query.orgId === "string" ? router.query.orgId : null;
  const seeded = useRef(false);

  useEffect(() => {
    if (!router.isReady || seeded.current || !orgId) return;
    seeded.current = true;
    setSelected([orgId]);
  }, [router.isReady, orgId]);

  const { data: source, error: sourceError } = useApi<PriorityMessage>(
    copyFrom ? `/api/priority-message/${copyFrom}` : null
  );

  const initialValues: FormValues = source
    ? {
        header: source.header,
        body: source.body,
        flags: parseFlags(source.flags),
        isGlobal: source.isGlobal,
      }
    : { ...emptyPriorityMessage, isGlobal: false };

  async function save(values: FormValues) {
    setError(null);
    setConflicts([]);

    const orgIds = values.isGlobal ? [] : selected;
    const blocked = await checkOrgConflicts(orgIds);

    if (blocked.length) {
      setConflicts(blocked);
      return;
    }

    const message = await post<
      {
        header: string;
        body: string;
        flags: string[];
        isGlobal: boolean;
      },
      PriorityMessage
    >("/api/priority-message", {
      header: values.header,
      body: values.body,
      flags: serializeFlags(values.flags),
      isGlobal: values.isGlobal,
    });

    setCreated(message);

    if (orgIds.length) {
      const result = await post<{ orgIds: string[] }, AssignResult>(
        `/api/priority-message/${message.messageId}/orgs`,
        { orgIds }
      );

      if (!result.assigned) {
        setConflicts(result.conflicts);
        setError(
          "The message was created, but an org was assigned something else in the meantime, so nothing was assigned."
        );
        return;
      }
    }

    openMessage(message);
  }

  function openMessage(message: PriorityMessage) {
    router.push(
      "/tools/priority-messages/[messageId]",
      `/tools/priority-messages/${message.messageId}`
    );
  }

  return (
    <Container className="mt-4">
      <Link href="/tools/priority-messages">
        <a>
          <ArrowLeftIcon /> All priority messages
        </a>
      </Link>

      <h1 className="mt-2">
        {copyFrom ? "Copy Priority Message" : "New Priority Message"}
      </h1>

      <Alert color="info">
        The message shows to customers at <strong>every location</strong> of
        each org you assign it to. It stays visible until you deactivate or
        archive it - there is no scheduling or expiry.
      </Alert>

      <div ref={outcomeRef}>
        <Alert
          color="danger"
          isOpen={error !== null}
          toggle={() => setError(null)}
        >
          {error}
        </Alert>

        {created && (
          <Button primary onClick={() => openMessage(created)}>
            Open the message
          </Button>
        )}
      </div>

      {sourceError && (
        <Alert color="danger">
          Could not load the message to copy: {sourceError.message}
        </Alert>
      )}

      {created || sourceError ? null : copyFrom && !source ? (
        <Spinner className="mt-4" />
      ) : (
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={priorityMessageSchema}
          onSubmit={async (values, helpers) => {
            try {
              await save({
                ...priorityMessageSchema.validateSync(values),
                isGlobal: values.isGlobal,
              });
            } catch (err) {
              setError((err as Error).message || "Unknown error");
            }
            helpers.setSubmitting(false);
          }}
        >
          <FormikForm>
            <PriorityMessageFields />

            <CheckboxField
              name="isGlobal"
              label="Region wide message"
              description="Show on every org in this region, with no assignment needed."
            />

            <Assignments
              selected={selected}
              onChange={setSelected}
              conflicts={conflicts}
            />

            <FormFooter orgCount={selected.length} />
          </FormikForm>
        </Formik>
      )}
    </Container>
  );
}

function Assignments(props: {
  selected: string[];
  onChange: (orgIds: string[]) => void;
  conflicts: OrgConflict[];
}) {
  const { values } = useFormikContext<FormValues>();

  if (values.isGlobal) return null;

  return (
    <OrganizationPicker
      label="Assign to orgs"
      selected={props.selected}
      onChange={props.onChange}
      conflicted={props.conflicts.map((conflict) => conflict.orgId)}
      message={
        props.conflicts.length > 0 && (
          <Alert color="danger">
            Oops! One or more selected organisations already has an assigned
            message. Deactivate the message an org has, or unpick it, then try
            again.
          </Alert>
        )
      }
    />
  );
}

function FormFooter(props: { orgCount: number }) {
  const { isSubmitting, values } = useFormikContext<FormValues>();

  return (
    <div
      className="position-sticky p-3 bg-light text-right"
      style={{ bottom: 0 }}
    >
      <span className="text-muted inline pr-2">
        {values.isGlobal
          ? "Shows immediately on every org in this region."
          : props.orgCount === 0
          ? "Not assigned to any org yet - you can assign it after creating."
          : `Shows immediately on ${props.orgCount} org${
              props.orgCount === 1 ? "" : "s"
            }.`}
      </span>
      <Button type="submit" primary disabled={isSubmitting}>
        {isSubmitting && (
          <>
            <Spinner size="sm" />{" "}
          </>
        )}
        Create Message
      </Button>
    </div>
  );
}
